"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { ArrowRight, Calendar, CircleCheck as CheckCircle2, Phone } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
const perks = [{
  id: 1,
  text: "Transparent estimates & timeline"
}, {
  id: 2,
  text: "Design-to-build team in Decatur"
}, {
  id: 3,
  text: "Clear written scope"
}];
export default function Planner() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="plan-your-project" data-nav="light" className="relative bg-background py-16 sm:py-20 lg:py-24 text-foreground overflow-hidden">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.25
      }} variants={containerVariants}>
          <Card className="relative overflow-hidden border border-border bg-card/90 backdrop-blur-md shadow-xl rounded-2xl py-8 sm:py-12 lg:py-14">
            {/* Top copper architectural hairline */}
            <div className="absolute top-0 inset-x-0 h-1 bg-chart-1" />

            {/* Subtle architectural background texture accent */}
            <div className="absolute -top-24 -right-24 size-96 rounded-full bg-chart-1/5 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-24 -left-24 size-96 rounded-full bg-primary/5 blur-3xl pointer-events-none" />

            <CardContent className="relative z-10 px-6 sm:px-12 lg:px-16 text-center max-w-4xl mx-auto flex flex-col items-center">
              <Badge variant="outline" className="mb-6 rounded-full border-border bg-secondary/70 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-foreground">
                Start your renovation
              </Badge>

              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-card-foreground leading-[1.15]">
                Ready to reimagine your Decatur home?
              </h2>

              <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed">
                Tell us about your whole-home renovation, addition, or finished
                basement. We will review your goals, discuss realistic budget
                ranges, and craft a detailed construction roadmap for your home.
              </p>

              {/* Action buttons */}
              <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4 w-full sm:w-auto">
                <Button asChild size="lg" className="w-full sm:w-auto px-7 py-6 text-base font-semibold shadow-md transition-[color,background-color,border-color,box-shadow]">
                  <Link to="#" className="inline-flex items-center gap-2">
                    Request an estimate
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>

                <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-border bg-secondary/80 text-secondary-foreground hover:bg-secondary px-7 py-6 text-base font-semibold transition-[color,background-color,border-color,box-shadow]">
                  <Link to="#" className="inline-flex items-center gap-2">
                    <SafeHydrate><Calendar className="size-4 text-chart-1" /></SafeHydrate>
                    View our planning process
                  </Link>
                </Button>
              </div>

              {/* Quick telephone direct contact fallback */}
              <div className="mt-6 flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <span>Prefer to call directly?</span>
                <a href="tel:+14708479076" className="inline-flex items-center gap-1.5 font-medium text-foreground hover:text-chart-1 transition-colors">
                  <Phone className="size-3.5 text-chart-1" />
                  (470) 847-9076
                </a>
              </div>

              {/* Quick assurance perks */}
              <div className="mt-10 pt-8 border-t border-border/80 grid grid-cols-1 sm:grid-cols-3 gap-4 w-full text-left sm:text-center">
                {perks.map((perk, i) => <div key={perk.id} data-index={i} className="flex items-center sm:justify-center gap-2 text-xs sm:text-sm font-medium text-muted-foreground">
                    <CheckCircle2 className="size-4 text-chart-1 shrink-0" />
                    <span>{perk.text}</span>
                  </div>)}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>;
}